import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import Text from '@components/Text';
import { t } from '@i18n';

import './styles.scss';

const BURN_MS = 1000;
const TYPE_MS = 3000;
// The name gets its own span, so the greeting template is split around a sentinel instead of
// interpolated — that keeps the name wherever the locale chooses to put it.
const NAME_SLOT = '\u0000';
// the name reveals more slowly than the words around it
const NAME_WEIGHT = 1.6;

type Phase = 'idle' | 'burning' | 'typing';
// 'full' types the whole greeting in (its first appearance); 'name' re-types only the name, in place
type Scope = 'full' | 'name';
type Part = { text: string; isName?: boolean; startUnits: number };

export function firstNameOf(fullName?: string) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';

  return first.length >= 2 ? first : '';
}

// Renders nothing until the buyer has a name; the static title below it never moves.
export default function CheckoutGreeting({ firstName }: { firstName: string }) {
  const [greeted, setGreeted] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [scope, setScope] = useState<Scope>('full');
  const [progress, setProgress] = useState(0);

  const rootRef = useRef<HTMLHeadingElement | null>(null);
  const nameRef = useRef<HTMLSpanElement | null>(null);
  const caretRef = useRef<HTMLSpanElement | null>(null);
  const pendingRef = useRef('');

  const parts = buildParts(greeted);
  const plan = buildPlan(parts);

  useEffect(() => {
    if (!firstName || firstName === greeted) return undefined;

    pendingRef.current = firstName;

    if (prefersReducedMotion()) {
      setGreeted(firstName);
      return undefined;
    }

    // First appearance: the line is empty, so there is nothing to burn — type straight in.
    if (!greeted) {
      setScope('full');
      setProgress(0);
      setGreeted(firstName);
      setPhase('typing');
      return undefined;
    }

    setScope('name');
    setPhase('burning');

    const timer = setTimeout(() => {
      const next = buildPlan(buildParts(pendingRef.current));

      setGreeted(pendingRef.current);
      // rewind before the first typing frame, or the name flashes in fully revealed
      setProgress(next.beforeUnits);
      setPhase('typing');
    }, BURN_MS);

    return () => clearTimeout(timer);
  }, [firstName, greeted]);

  useEffect(() => {
    if (phase !== 'typing') return undefined;

    if (prefersReducedMotion()) {
      setProgress(plan.totalUnits);
      setPhase('idle');
      return undefined;
    }

    // a name-only swap leaves the phrase on screen, so the clock starts past it
    const startUnits = scope === 'name' ? plan.beforeUnits : 0;
    const startedAt = performance.now();
    let frame = requestAnimationFrame(function step(now: number) {
      const units = startUnits + (now - startedAt) / plan.msPerUnit;

      if (units >= plan.totalUnits) {
        setProgress(plan.totalUnits);
        setPhase('idle');
        return;
      }

      setProgress(units);
      frame = requestAnimationFrame(step);
    });

    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, greeted, scope]);

  const nameProgress = phase !== 'typing' || plan.nameUnits === 0
    ? 1
    : clamp((progress - plan.beforeUnits) / plan.nameUnits, 0, 1);

  // The caret rides the reveal: it sits at the trailing edge of the last typed character, or at the
  // wipe edge inside the name. Positioned imperatively so a frame costs one render, not two.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const caret = caretRef.current;

    if (!root || !caret || phase !== 'typing') return;

    const chars = Array.from(root.querySelectorAll<HTMLElement>('[data-unit]'));
    const unitOf = (element: HTMLElement) => Number(element.dataset.unit);
    const lastTypedIn = (from: number, until: number) => chars
      .filter((element) => unitOf(element) >= from && unitOf(element) < Math.min(progress, until))
      .pop();

    const nameEnd = plan.beforeUnits + plan.nameUnits;
    let anchor: HTMLElement | null;
    let x: number;

    if (progress < plan.beforeUnits) {
      const last = lastTypedIn(0, plan.beforeUnits);
      anchor = last || chars[0] || null;
      x = anchor ? (last ? anchor.offsetLeft + anchor.offsetWidth : anchor.offsetLeft) : 0;
    } else if (nameRef.current && nameProgress < 1) {
      anchor = nameRef.current;
      x = anchor.offsetLeft + nameProgress * anchor.offsetWidth;
    } else {
      // past the name: sit after the trailing text if the locale has any, else at the name's edge
      anchor = lastTypedIn(nameEnd, Infinity) || nameRef.current || chars[chars.length - 1] || null;
      x = anchor ? anchor.offsetLeft + anchor.offsetWidth : 0;
    }

    if (!anchor) return;

    // the name sits on its own line at a larger size, so the caret takes its height from
    // whatever it is currently riding rather than from a fixed em
    const height = Math.round(anchor.offsetHeight * 0.78);

    caret.style.height = `${height}px`;
    caret.style.left = `${x}px`;
    caret.style.top = `${anchor.offsetTop + (anchor.offsetHeight - height) / 2}px`;
  }, [progress, nameProgress, phase, plan.beforeUnits, plan.nameUnits]);

  if (parts.length === 0) {
    return null;
  }

  return (
    <Text.h3
      ref={rootRef}
      key={`${phase === 'burning' ? 'out' : 'in'}-${greeted}`}
      className={`checkout-greeting checkout-greeting--${phase}`}
      aria-label={parts.map((part) => part.text).join('')}
    >
      {parts.map((part, index) => (
        part.isName
          ? (
            // the name owns its own right-aligned line, but the clip-path wipe and the caret both
            // measure glyphs — so the block is the wrapper and the name itself stays inline-block
            <span key={index} aria-hidden="true" className="checkout-greeting-line">
              <span
                ref={nameRef}
                className="checkout-greeting-name"
                style={nameProgress < 1 ? { clipPath: `inset(0 ${(1 - nameProgress) * 100}% -25% 0)` } : undefined}
              >
                {part.text}
              </span>
            </span>
          )
          : (
            <SplitText
              key={index}
              text={part.text}
              startUnits={part.startUnits}
              revealed={phase !== 'typing'}
              progress={progress}
            />
          )
      ))}

      {phase === 'typing' && <span ref={caretRef} aria-hidden="true" className="checkout-greeting-caret" />}
    </Text.h3>
  );
}

function SplitText({ text, startUnits, revealed, progress }: {
  text: string;
  startUnits: number;
  revealed: boolean;
  progress: number;
}) {
  let cursor = 0;

  // whitespace is kept outside the word spans, so a line can only break between words
  return (
    <>
      {text.split(/(\s+)/).filter(Boolean).map((chunk, chunkIndex) => {
        if (/^\s+$/.test(chunk)) {
          cursor += chunk.length;
          return <span key={chunkIndex} aria-hidden="true" className="checkout-greeting-space">{chunk}</span>;
        }

        const offset = cursor;
        const characters = Array.from(chunk);
        cursor += characters.length;

        return (
          <span key={chunkIndex} aria-hidden="true" className="checkout-greeting-word">
            {characters.map((character, index) => {
              const typed = revealed || progress > startUnits + offset + index;

              return (
                <span
                  key={index}
                  // the clock counts whitespace but these spans do not, so each one carries the
                  // unit it belongs to rather than relying on its position in the list
                  data-unit={startUnits + offset + index}
                  className={`checkout-greeting-char${typed ? ' is-typed' : ''}`}
                >
                  {character}
                </span>
              );
            })}
          </span>
        );
      })}
    </>
  );
}

function buildParts(firstName: string): Part[] {
  if (!firstName) {
    return [];
  }

  let startUnits = 0;

  return splitGreeting(firstName).map((part) => {
    const entry: Part = { ...part, startUnits };

    startUnits += lengthOf(part.text) * (part.isName ? NAME_WEIGHT : 1);

    return entry;
  });
}

function splitGreeting(firstName: string) {
  const [before, after = ''] = t('checkout.greeting', { firstName: NAME_SLOT }).split(NAME_SLOT);

  return [{ text: before }, { text: firstName, isName: true }, { text: after }]
    .filter((part) => part.text.length > 0);
}

// The whole line lands in TYPE_MS whatever the locale's word order or the length of the name,
// and a name-only swap re-uses the same per-character rate so it feels identical.
function buildPlan(parts: Part[]) {
  const name = parts.find((part) => part.isName);
  const totalUnits = parts.reduce((sum, part) => sum + lengthOf(part.text) * (part.isName ? NAME_WEIGHT : 1), 0) || 1;

  return {
    beforeUnits: name ? name.startUnits : 0,
    nameUnits: name ? lengthOf(name.text) * NAME_WEIGHT : 0,
    totalUnits,
    msPerUnit: TYPE_MS / totalUnits,
  };
}

const lengthOf = (text: string) => Array.from(text).length;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
